/**
 * Favorites are cocktail names (stable across Sheets row reorder).
 * Older localStorage values used ephemeral ids like "recipe-2" / "curated-1".
 */

export const favoriteKey = (drink) => (drink?.name || "").trim();

export const toggleFavorite = (favorites, drink) => {
    const key = favoriteKey(drink);
    if (!key) return Array.isArray(favorites) ? favorites : [];
    const list = Array.isArray(favorites) ? favorites : [];
    return list.includes(key) ? list.filter((name) => name !== key) : [...list, key];
};

export const isFavorite = (favorites, drink) => {
    const key = favoriteKey(drink);
    return Boolean(key && Array.isArray(favorites) && favorites.includes(key));
};

/** Build name → drink map (prefer full library recipe when both exist). */
export const drinksByFavoriteKey = (menuData) => {
    const map = new Map();
    for (const drink of menuData?.curated || []) {
        const key = favoriteKey(drink);
        if (key) map.set(key, drink);
    }
    for (const drink of menuData?.recipes || []) {
        const key = favoriteKey(drink);
        if (key) map.set(key, drink);
    }
    return map;
};

/**
 * Resolve saved favorites to drinks. Migrates legacy id keys ("recipe-N")
 * to cocktail names when the current menu still has that id.
 * Returns { drinks, favorites: string[] } — use favorites if it differs to persist migration.
 */
export const resolveFavorites = (favorites, menuData) => {
    const list = Array.isArray(favorites) ? favorites : [];
    const byName = drinksByFavoriteKey(menuData);
    const byId = new Map();
    for (const drink of [...(menuData?.recipes || []), ...(menuData?.curated || [])]) {
        if (drink?.id) byId.set(drink.id, drink);
    }

    const drinks = [];
    const nextKeys = [];
    const seen = new Set();

    for (const entry of list) {
        if (!entry || typeof entry !== "string") continue;

        let drink = byName.get(entry);
        if (!drink && byId.has(entry)) {
            drink = byId.get(entry);
        }
        if (!drink) continue;

        const key = favoriteKey(drink);
        if (!key || seen.has(key)) continue;
        seen.add(key);
        nextKeys.push(key);
        drinks.push(drink);
    }

    return { drinks, favorites: nextKeys };
};
